import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getServiceMessages, createServiceMessage, createAnnouncement } from '../lib/api';
import { 
  Megaphone,
  PaperPlaneTilt,
  Bell,
  EnvelopeSimple,
  Users,
  UserGear,
  House,
  Clock,
  Info,
  Wrench,
  DeviceMobile,
  Globe
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

interface ServiceMessageData {
  id: string;
  title?: string;
  message?: string;
  category?: string;
  target?: string;
  created_at?: string;
  created_by_name?: string;
  push_count?: number;
  push_sent?: number;
  email_count?: number;
  email_sent?: number;
  sms_count?: number;
  sms_sent?: number;
}

// Target icon helper
const TargetIcon = ({ size }: { size: number }) => <Users size={size} />;

export default function ServiceMessagesPage() {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState<ServiceMessageData[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  
  const [form, setForm] = useState({
    title: '',
    message: '',
    category: 'info',
    target: 'all',
    send_push: true,
    send_email: false,
    send_sms: false,
    publish_to_portal: false
  });

  const fetchHistory = async () => {
    try {
      const { data } = await getServiceMessages({ limit: 10 });
      setHistory(data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleSend = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Ange titel och meddelande');
      return;
    }
    
    setSending(true);
    try {
      const { data } = await createServiceMessage(form);
      const result = data as ServiceMessageData;
      
      // If publish_to_portal is checked, also create an announcement
      if (form.publish_to_portal && user?.organization_id) {
        try {
          await createAnnouncement({
            title: form.title,
            content: form.message,
            category: form.category === 'info' ? 'news' : form.category === 'drift' ? 'important' : 'news',
            organization_id: user.organization_id,
            created_by: user.id,
            created_by_name: user.name,
            is_published: true,
            is_public: true // Visible on public portal
          });
        } catch (announcementError) {
          console.error('Failed to create announcement:', announcementError);
          // Don't fail the whole operation if announcement creation fails
        }
      }
      
      // Build success message based on what was sent
      const successParts: string[] = [];
      if ((result.push_sent ?? 0) > 0) {
        successParts.push(`${result.push_sent} push-notiser`);
      }
      if ((result.email_sent ?? 0) > 0) {
        successParts.push(`${result.email_sent} e-post`);
      }
      if ((result.sms_sent ?? 0) > 0) {
        successParts.push(`${result.sms_sent} SMS`);
      }
      if (form.publish_to_portal) {
        successParts.push('publicerad på portalen');
      }
      const sentSummary = successParts.length > 0 ? ` (${successParts.join(', ')})` : '';
      toast.success(`${result.message || 'Meddelande skickat'}${sentSummary}`);
      setForm({ title: '', message: '', category: 'info', target: 'all', send_push: true, send_email: false, send_sms: false, publish_to_portal: false });
      fetchHistory();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err.response?.data?.detail || 'Kunde inte skicka meddelande');
    } finally {
      setSending(false);
    }
  };

  // Category configuration
  const categoryConfig: Record<string, { label: string; icon: typeof Info; color: string; bg: string }> = {
    info: { label: 'Information', icon: Info, color: 'text-blue-600', bg: 'bg-blue-100' },
    drift: { label: 'Driftinformation', icon: Wrench, color: 'text-amber-600', bg: 'bg-amber-100' },
    custom: { label: 'Eget', icon: Megaphone, color: 'text-purple-600', bg: 'bg-purple-100' }
  };

  const targetLabels: Record<string, string> = {
    all: 'Alla medlemmar',
    admins: 'Endast styrelse/admin',
    residents: 'Endast boende'
  };

  const targetIcons: Record<string, React.ComponentType<{ size: number }>> = {
    all: Users,
    admins: UserGear,
    residents: House
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('sv-SE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6 max-w-4xl" data-testid="service-messages-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Megaphone size={24} className="text-blue-600" />
          Servicemeddelande
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Skicka meddelanden till alla i föreningen via push-notiser, e-post och SMS
        </p>
      </div>

      {/* Compose Form */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
          <PaperPlaneTilt size={16} />
          Nytt meddelande
        </div>
        
        <div className="space-y-4">
          {/* Category Selection */}
          <div className="space-y-2">
            <Label className="text-xs">Kategori</Label>
            <Select 
              value={form.category} 
              onValueChange={(v) => setForm(prev => ({ ...prev, category: v }))}
            >
              <SelectTrigger className="h-10" data-testid="service-message-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">
                  <span className="flex items-center gap-2">
                    <Info size={14} className="text-blue-600" />
                    Information
                  </span>
                </SelectItem>
                <SelectItem value="drift">
                  <span className="flex items-center gap-2">
                    <Wrench size={14} className="text-amber-600" />
                    Driftinformation
                  </span>
                </SelectItem>
                <SelectItem value="custom">
                  <span className="flex items-center gap-2">
                    <Megaphone size={14} className="text-purple-600" />
                    Eget
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Titel *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder={form.category === 'drift' ? 'T.ex. Vattenavstängning' : form.category === 'info' ? 'T.ex. Städdag 15 april' : 'Ange rubrik'}
              className="h-10"
              data-testid="service-message-title"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-xs">Meddelande *</Label>
            <Textarea
              value={form.message}
              onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Skriv ditt meddelande här..."
              className="min-h-[100px]"
              data-testid="service-message-body"
            />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Mottagare</Label>
              <Select 
                value={form.target} 
                onValueChange={(v) => setForm(prev => ({ ...prev, target: v }))}
              >
                <SelectTrigger className="h-10" data-testid="service-message-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="flex items-center gap-2">
                      <Users size={14} />
                      Alla medlemmar
                    </span>
                  </SelectItem>
                  <SelectItem value="admins">
                    <span className="flex items-center gap-2">
                      <UserGear size={14} />
                      Endast styrelse/admin
                    </span>
                  </SelectItem>
                  <SelectItem value="residents">
                    <span className="flex items-center gap-2">
                      <House size={14} />
                      Endast boende
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-3 pt-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send_push"
                  checked={form.send_push}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, send_push: !!checked }))}
                />
                <label htmlFor="send_push" className="text-sm text-slate-700 flex items-center gap-1">
                  <Bell size={14} />
                  Push-notis
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send_email"
                  checked={form.send_email}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, send_email: !!checked }))}
                />
                <label htmlFor="send_email" className="text-sm text-slate-700 flex items-center gap-1">
                  <EnvelopeSimple size={14} />
                  E-post
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send_sms"
                  checked={form.send_sms}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, send_sms: !!checked }))}
                />
                <label htmlFor="send_sms" className="text-sm text-slate-700 flex items-center gap-1">
                  <DeviceMobile size={14} />
                  SMS
                </label>
              </div>
              
              <div className="pt-3 mt-3 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="publish_to_portal"
                    checked={form.publish_to_portal}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, publish_to_portal: !!checked }))}
                  />
                  <label htmlFor="publish_to_portal" className="text-sm text-slate-700 flex items-center gap-1">
                    <Globe size={14} className="text-green-600" />
                    Publicera på portalen
                  </label>
                </div>
                <p className="text-xs text-slate-500 ml-6 mt-1">
                  Meddelandet visas som nyhet på föreningens publika portal
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pt-4 border-t border-slate-100">
          <Button 
            onClick={handleSend} 
            disabled={sending || !form.title.trim() || !form.message.trim()}
            className="w-full sm:w-auto"
            data-testid="send-service-message"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Skickar...
              </span>
            ) : (
              <>
                <Megaphone size={16} className="mr-2" />
                Skicka meddelande
              </>
            )}
          </Button>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-4">
          <Clock size={16} />
          Senaste meddelanden
        </div>
        
        {loadingHistory ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-4 w-32 bg-slate-200 rounded mb-2" />
                <div className="h-3 w-48 bg-slate-100 rounded" />
              </div>
            ))}
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">
            Inga meddelanden skickade ännu
          </p>
        ) : (
          <div className="space-y-3">
            {history.map((msg) => {
              const TargetIcon = targetIcons[msg.target || 'all'] || Users;
              const catConfig = categoryConfig[msg.category || 'info'] || categoryConfig.info;
              const CategoryIcon = catConfig.icon;
              return (
                <div 
                  key={msg.id}
                  className="p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`${catConfig.bg} ${catConfig.color} px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1`}>
                          <CategoryIcon size={10} weight="bold" />
                          {catConfig.label}
                        </span>
                        <span className="font-medium text-slate-900 text-sm">
                          {msg.title}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 flex items-center gap-1">
                          <TargetIcon size={10} />
                          {targetLabels[msg.target || 'all']}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {msg.message}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400 flex-wrap">
                        <span>{formatDate(msg.created_at)}</span>
                        <span>•</span>
                        <span>{msg.created_by_name || 'Admin'}</span>
                        {(msg.push_count ?? 0) > 0 && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1 text-green-600">
                              <Bell size={10} />
                              {msg.push_count} notiser
                            </span>
                          </>
                        )}
                        {(msg.email_count ?? 0) > 0 && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1 text-blue-600">
                              <EnvelopeSimple size={10} />
                              {msg.email_count} e-post
                            </span>
                          </>
                        )}
                        {(msg.sms_count ?? 0) > 0 && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1 text-purple-600">
                              <DeviceMobile size={10} />
                              {msg.sms_count} SMS
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
